import { type CompanyTag, type CompanyTagCategory } from "@prisma/client";
import classNames from "classnames";
import React from "react";
import { SelectInput } from "~/components/ui/core/BetterSelectInput";
import { Icon } from "~/components/ui/core/Icon";
import { useI18n } from "~/lib/i18n/useI18n";
import { chain } from "~/lib/lodash";
import { getId } from "~/lib/utils";
import { useListCompanyTagsQuery } from "~/pages/api/list-company-tags";
import { formatCompanyTagCategory } from "~/services/companyTags";

type Event = {
  target: {
    name?: string;
    value: CompanyTag[];
  };
};

type Props = {
  name?: string;
  categories?: CompanyTagCategory[];
  multiple?: boolean;
  placeholder?: string;
  value: CompanyTag[];
  excludedValues?: CompanyTag[];
  disabled?: boolean;
  onChange: (event: Event) => void;
  hideGroupLabels?: boolean;
};

export const LegacyCompanyTagSelector: React.FC<Props> = ({
  name,
  value,
  categories = [],
  excludedValues = [],
  multiple = false,
  disabled = false,
  onChange,
  placeholder,
  hideGroupLabels = false,
}) => {
  const { t } = useI18n();
  const { data: tagData } = useListCompanyTagsQuery();

  if (!placeholder) {
    placeholder = t("components.ui.legacy-company-tag-selector.placeholder");
  }

  const groups = chain(tagData ?? [])
    .filter((tag) => {
      if (!categories.length) {
        return true;
      }

      return categories.includes(tag.category);
    })
    .groupBy(({ category }) => category)
    .values()
    .map((tags) => {
      const templateCategory = tags[0]?.category as CompanyTagCategory;

      return {
        label: formatCompanyTagCategory(templateCategory),
        options: tags.filter((tag) => {
          const isExcluded = !!excludedValues.find(({ id }) => id === tag.id);
          if (isExcluded) {
            return false;
          }

          return true;
        }),
      };
    })
    .value();

  return (
    <SelectInput
      dense
      disabled={disabled}
      hideGroupLabels={groups.length === 1 && hideGroupLabels}
      formatLabel={(tags) => {
        const label = tags.map(({ name }) => name).join(", ");

        return (
          <div
            className={classNames({
              "relative flex w-full items-center overflow-hidden whitespace-nowrap rounded border border-gray-300 bg-white py-1 px-2 text-left text-sm outline-none focus-within:ring hover:border-primary-400":
                true,
              "text-gray-700": !label,
            })}
          >
            <Icon name="filter" className="mr-1 text-gray-700" />
            {label || placeholder}
          </div>
        );
      }}
      formatOption={(tag) => {
        return (
          <span className="flex flex-col">
            <span>{tag.name} </span>
            {tag.description !== null && (
              <span className="mt-1 whitespace-normal text-xs text-gray-500">{tag.description}</span>
            )}
          </span>
        );
      }}
      groups={groups}
      multiple={multiple}
      name={name}
      optionToId={getId}
      placeholder={
        multiple
          ? t("components.ui.legacy-company-tag-selector.employee-location_other")
          : t("components.ui.legacy-company-tag-selector.employee-location_one")
      }
      searchOptions={{
        optionToSearchableStrings: (tag) => {
          const strings = [tag.name];

          if (tag.description) {
            strings.push(tag.description);
          }

          return strings;
        },
        placeholder: t("components.ui.legacy-company-tag-selector.search-tags"),
      }}
      value={value}
      width="xl"
      onChange={(event) => {
        onChange(event);
      }}
    />
  );
};
